import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Checkbox, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { Link } from 'react-router-dom';
import { EllipsisOutlined, EditOutlined, ShareAltOutlined, DownloadOutlined, DeleteOutlined, PushpinFilled } from '@ant-design/icons';
import { ReactComponent as ImagePlaceholder } from '../../images/placeholder-img.svg';
import { getEntryOrganizationLabel } from '../../utils/projectFilterSortHelpers';
import { getUserDisplayNameWithDeleted, isUserDeleted } from '../../utils/userPresence';
import {
  canEditProjectCardMenu,
  canShowDeleteInCardMenu,
  canShowDownloadInCardMenu,
  normalizeCardMenuDividers,
} from '../../utils/entryCardMenuPermissions';
import { useAuth } from '../../hooks/useAuth';
import { DeletedUserAvatarShell } from '../UserPresence/DeletedUserAvatarShell';
import styles from './ProjectCard.module.scss';

/** Geçici: çift tıklama ile rename kapalı. Tekrar açmak için `true` yapın. */
const DOUBLE_CLICK_RENAME_ENABLED = false;

export interface ProjectCardProject {
  id: number;
  name: string;
  path: string;
  organization?: string | null;
  isPinned?: boolean;
  /** Alt öğe sayısı (all servisi ile gelir) */
  childrenEntryCount?: number | null;
  /** Cover/thumbnail image URL (API bazen thumbnailUrl döndürür) */
  thumbnail?: string | null;
  thumbnailUrl?: string | null;
  share?: Array<{
    id?: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    thumbnailUrl?: string;
    organizationName?: string;
    isDeleted?: boolean;
  }>;
  updatedAt?: string | Date | null;
  createdAt?: string | Date | null;
}

export type ProjectCardMenuAction = 'rename' | 'changeCover' | 'share' | 'download' | 'delete';

interface ProjectCardProps {
  project: ProjectCardProject;
  projectsBasePath: string;
  isSelected: boolean;
  onSelectChange: (checked: boolean) => void;
  formatDate: (value: string | Date | undefined | null) => string;
  showCheckbox?: boolean;
  canMultiSelect?: boolean;
  selectedCount?: number;
  disabled?: boolean;
  onMenuAction?: (action: ProjectCardMenuAction, project: ProjectCardProject) => void;
  editingProjectId?: number | null;
  onRenameSubmit?: (project: ProjectCardProject, newName: string) => void;
  onRenameCancel?: () => void;
  /** 'external' = External Projects sayfası (mor tema) */
  variant?: 'internal' | 'external';
  /** Sağ üst pushpin tıklanınca (fonksiyon sonra belirlenecek) */
  onPinClick?: (project: ProjectCardProject) => void;
  /** false: pin/unpin yok; pinli projede salt okunur pin rozeti gösterilir */
  canTogglePin?: boolean;
}

const ICON_SIZE = 18;

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  projectsBasePath,
  isSelected,
  onSelectChange,
  formatDate,
  showCheckbox = true,
  canMultiSelect = false,
  selectedCount = 0,
  disabled = false,
  onMenuAction,
  editingProjectId = null,
  onRenameSubmit,
  onRenameCancel,
  variant = 'internal',
  onPinClick,
  canTogglePin = true
}) => {
  const { user } = useAuth();
  const effectiveDate = project?.updatedAt ?? project?.createdAt;
  const dateStr = formatDate(effectiveDate ?? undefined);
  const projectOrganizationLabel = getEntryOrganizationLabel(project);
  const isEditing = editingProjectId === project.id;
  const [editValue, setEditValue] = useState(project.name);
  const [menuOpen, setMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      setEditValue(project.name);
      inputRef.current?.focus();
      // Focus sonrası tüm metni seç (DOM güncellendikten sonra)
      const t = setTimeout(() => {
        inputRef.current?.select();
      }, 0);
      return () => clearTimeout(t);
    }
  }, [isEditing, project.name]);

  const handleBlurOrSubmit = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== project.name) {
      onRenameSubmit?.(project, trimmed);
    } else {
      onRenameCancel?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlurOrSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onRenameCancel?.();
    }
  };

  const handleMenuClick = (action: ProjectCardMenuAction) => {
    setMenuOpen(false);
    onMenuAction?.(action, project);
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if ((e.target as HTMLElement).closest('[data-no-nav]')) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const menuItems: MenuProps['items'] = useMemo(() => {
    const canEdit = canEditProjectCardMenu(user?.role);
    const canDelete = canShowDeleteInCardMenu(user?.role);
    const canDownload = canShowDownloadInCardMenu(!!user);
    const items: NonNullable<MenuProps['items']> = [];
    if (canEdit) {
      items.push(
        {
          key: 'rename',
          label: 'Rename',
          icon: (
            <span
              className={styles.cardMenuSvgIcon}
              style={{
                WebkitMaskImage: 'url(/images/icon/rename.svg)',
                maskImage: 'url(/images/icon/rename.svg)',
              }}
              aria-hidden
            />
          ),
        },
        {
          key: 'changeCover',
          label: 'Change Cover',
          icon: <EditOutlined style={{ fontSize: ICON_SIZE }} />,
        },
        { type: 'divider' },
        {
          key: 'share',
          label: 'Assign',
          icon: <ShareAltOutlined style={{ fontSize: ICON_SIZE }} />,
        }
      );
    }
    if (canDownload) {
      items.push({
        key: 'download',
        label: 'Download',
        icon: <DownloadOutlined style={{ fontSize: ICON_SIZE }} />,
      });
    }
    if (canDelete) {
      items.push({
        key: 'delete',
        label: 'Delete',
        icon: <DeleteOutlined style={{ fontSize: ICON_SIZE }} />,
        danger: true,
      });
    }
    return normalizeCardMenuDividers(items);
  }, [user?.role]);

  const hasCardMenuItems = useMemo(
    () =>
      (menuItems || []).some(
        (it) =>
          !!it &&
          typeof it === 'object' &&
          (!('type' in it) || (it as { type?: string }).type !== 'divider')
      ),
    [menuItems]
  );

  const cardContent = (
    <div className={styles.cardRow}>
      {/* Sol: cover/thumbnail — tıklanınca detaya git; yalnızca checkbox data-no-nav ile ayrı */}
      <div className={`${styles.leftCol} ${variant === 'external' ? styles.leftColExternal : ''}`}>
        <div className={styles.leftColImage}>
          {(project.thumbnail ?? project.thumbnailUrl) ? (
            <img src={(project.thumbnail ?? project.thumbnailUrl) as string} alt="" className={styles.leftColImageImg} />
          ) : (
            <ImagePlaceholder className={styles.leftColPlaceholder} />
          )}
        </div>
        {showCheckbox && (
          <span
            data-no-nav
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={isSelected}
              onChange={(e) => onSelectChange(e.target.checked)}
              disabled={disabled || (selectedCount > 0 && !isSelected && !canMultiSelect)}
              className={styles.selectCheckbox}
              onClick={(e) => e.stopPropagation()}
            />
          </span>
        )}
      </div>
      {/* Sağ: 16/24 - üst: user iconları, orta: proje adı, alt: tarih (space-between) */}
      <div className={styles.rightCol}>
        {/* Üst: User avatar'ları */}
        <div className={styles.rightTop}>
          {project.share && project.share.length > 0 && (
            <div className={styles.shareAvatarsWrapper}>
              {project.share.slice(0, 3).map((shareItem: any, idx: number) => {
                const shDel = isUserDeleted(shareItem);
                return (
                <div
                  key={shareItem.id ?? idx}
                  className={styles.shareAvatar}
                  style={{
                    backgroundColor: shareItem.thumbnailUrl ? '#000' : 'var(--app-accent-color)',
                    marginLeft: idx > 0 ? -8 : 0,
                    zIndex: 3 - idx
                  }}
                  title={getUserDisplayNameWithDeleted(shareItem, null)}
                >
                  <DeletedUserAvatarShell deleted={shDel}>
                    {shareItem.thumbnailUrl ? (
                      <img
                        src={shareItem.thumbnailUrl}
                        alt={getUserDisplayNameWithDeleted(shareItem, null)}
                        style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span className={styles.shareAvatarLetter}>
                        {(shareItem.firstName?.[0] ?? shareItem.email?.[0] ?? 'U').toUpperCase()}
                      </span>
                    )}
                  </DeletedUserAvatarShell>
                </div>
              );
              })}
              {project.share.length > 3 && (
                <div className={styles.shareAvatarMore}>+{project.share.length - 3}</div>
              )}
            </div>
          )}
        </div>
        {/* Orta: Proje adı (inline rename: isEditing ise input) */}
        {isEditing ? (
          <div
            className={`${styles.projectNameWrap}${projectOrganizationLabel ? ` ${styles.projectNameWrapWithOrg}` : ''}`}
            data-no-nav
            onClick={(e) => e.stopPropagation()}
          >
            <input
              ref={inputRef}
              type="text"
              className={styles.projectNameInput}
              data-no-nav
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleBlurOrSubmit}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              aria-label="Project name"
            />
          </div>
        ) : (
          <div
            className={styles.projectNameBlock}
            onDoubleClick={
              DOUBLE_CLICK_RENAME_ENABLED
                ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onMenuAction?.('rename', project);
                  }
                : undefined
            }
            title={DOUBLE_CLICK_RENAME_ENABLED ? 'Double-click to rename' : undefined}
          >
            <span className={styles.projectName}>{project.name}</span>
            {projectOrganizationLabel ? (
              <span className={styles.projectOrganization}>{projectOrganizationLabel}</span>
            ) : null}
          </div>
        )}
        {/* Alt: tarih/entry sayısı tıklanınca detaya git; sadece ⋯ menüsü data-no-nav */}
        <div className={styles.rightBottom}>
          <div className={styles.cardMetaRow}>
            {project.childrenEntryCount != null && project.childrenEntryCount > 0 && (
              <span className={styles.childrenEntryCount}>
                {project.childrenEntryCount === 1 ? '1 entry' : `${project.childrenEntryCount} entries`}
              </span>
            )}
            <span className={styles.projectDate}>{dateStr}</span>
          </div>
          {hasCardMenuItems ? (
            <span
              className={styles.menuIconWrap}
              data-no-nav
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Dropdown
                open={menuOpen}
                onOpenChange={setMenuOpen}
                menu={{
                  items: menuItems,
                  className: `${styles.cardMenuOverlay} ${variant === 'external' ? styles.cardMenuOverlayExternal : ''}`,
                  onClick: ({ key }) => handleMenuClick(key as ProjectCardMenuAction),
                }}
                trigger={['click']}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              >
                <button type="button" className={styles.cardMenuTrigger} aria-label="Menu" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                  <EllipsisOutlined style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)' }} />
                </button>
              </Dropdown>
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );

  const projectCardShell = (
    <div className={`${styles.projectCard} ${variant === 'external' ? styles.projectCardExternal : ''}`}>
      {canTogglePin ? (
        <button
          type="button"
          className={`${styles.cardPinButton} ${project?.isPinned ? styles.cardPinButtonPinned : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onPinClick?.(project);
          }}
          aria-label={project?.isPinned ? 'Unpin' : 'Pin'}
          data-no-nav
        >
          <PushpinFilled className={styles.cardPinIcon} />
        </button>
      ) : project?.isPinned ? (
        <span
          className={`${styles.cardPinButton} ${styles.cardPinButtonPinned} ${styles.cardPinReadOnly}`}
          aria-label="Pinned project"
          role="img"
        >
          <PushpinFilled className={styles.cardPinIcon} />
        </span>
      ) : null}
      {cardContent}
    </div>
  );

  const cardInner = isEditing ? (
    <div className={styles.projectCardLink} role="presentation">
      {projectCardShell}
    </div>
  ) : (
    <Link
      to={`/${project.path}`}
      state={{ fromProjectsBasePath: projectsBasePath }}
      className={styles.projectCardLink}
      draggable={false}
      onClick={handleLinkClick}
      onMouseDown={handleLinkClick}
    >
      {projectCardShell}
    </Link>
  );

  return hasCardMenuItems ? (
    <Dropdown
      trigger={['contextMenu']}
      menu={{
        items: menuItems,
        className: `${styles.cardMenuOverlay} ${variant === 'external' ? styles.cardMenuOverlayExternal : ''}`,
        onClick: ({ key }) => handleMenuClick(key as ProjectCardMenuAction),
      }}
      getPopupContainer={() => document.body}
    >
      <div className={styles.contextMenuTrigger} onContextMenu={(e) => e.preventDefault()}>
        {cardInner}
      </div>
    </Dropdown>
  ) : (
    <div className={styles.contextMenuTrigger} onContextMenu={(e) => e.preventDefault()}>
      {cardInner}
    </div>
  );
};

export default ProjectCard;
